import React from 'react';
import { Box, Typography, Button, Chip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ProgressArc from './ProgressArc';

/**
 * ChallengeHero — kluczowy komponent "poziomu Hero" wg sekcji 3.2 i 8.1
 * dokumentu UI Design Direction. Prezentuje aktywną kampanię jako
 * pełnoprawny, wyróżniony moment, nie kolejną kartę statystyk.
 */
function ChallengeHero({
  title,
  statusLabel = 'Aktywna',
  imageUrl,
  metricValue,
  metricLabel,
  progressValue,
  participantsValue,
  participantsLabel = 'uczestników',
  ctaLabel = 'Zobacz wyniki',
  onCtaClick,
}) {
  const theme = useTheme();
  const heroTokens = theme.tokens?.components?.challengeHero;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        borderRadius: `${heroTokens?.radius ?? 30}px`,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: heroTokens?.shadow,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: 'background.paper',
      }}
    >
      {imageUrl && (
        <Box
          sx={{
            width: { xs: '100%', md: 260 },
            minHeight: { xs: 160, md: 'auto' },
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <Box sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
        <Chip
          label={statusLabel}
          color="success"
          size="small"
          sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6875rem' }}
        />
        <Typography
          variant="h3"
          sx={{
            fontFamily: theme.typography.h1.fontFamily,
            mb: 3,
            maxWidth: { xs: '100%', md: 480 },
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: { xs: 2.5, md: 5 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                lineHeight: 1,
                fontSize: { xs: '2.25rem', md: '2.75rem' },
                wordBreak: 'break-word',
              }}
            >
              {metricValue}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {metricLabel}
            </Typography>
          </Box>

          {typeof progressValue === 'number' && (
            <ProgressArc
              value={progressValue}
              label={`${progressValue}%`}
              sublabel="celu"
              size={isMobile ? 68 : 84}
            />
          )}

          {participantsValue != null && (
            <Box>
              <Typography variant="h5" sx={{ fontFamily: theme.typography.h1.fontFamily }}>
                {participantsValue}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {participantsLabel}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={onCtaClick}
            fullWidth
            sx={{
              ml: { md: 'auto' },
              width: { xs: '100%', md: 'auto' },
              borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px`,
              px: 3,
              order: { xs: 10, md: 0 },
            }}
          >
            {ctaLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ChallengeHero;
